import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AnimatedPage from '@/components/AnimatedPage';
import FinanceTable from './components/FinanceTable';
import FinanceDialog from './components/FinanceDialog';
import PaymentSystemOutSettingsDialog from './components/PaymentSystemOutSettingsDialog';
import PaymentSystemInSettingsDialog from './components/PaymentSystemInSettingsDialog';
import PaymentMethodMap from './components/PaymentMethodMap';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import { ExchangeRates } from './';
import IndividualExchangeRates, {
  type IndividualExchangeRateRow,
} from './components/IndividualExchangeRates';
import { useFinance } from './hooks/useFinance';
import { dictionariesService } from '@/services/dictionaries/dictionaries.service';
import { financeService } from '@/services/finance/finance.service';
import { tradersService } from '@/services/traders/traders.service';
import { merchantsService } from '@/services/merchants/merchants.service';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';
import { useNotification } from '@/contexts/NotificationContext';
import { ExchangeRate, MerchantExchangeRate, TraderExchangeRate } from '@/types/finance';
import { Trader } from '@/types/trader';
import type { Merchant } from '@/types/merchant';
import { RoleEnum } from '@/types/auth/roles';
import { useSearchParams } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const Finance: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedMethodId = searchParams.get('method');
  const [tabValue, setTabValue] = useState(searchParams.get('tab') === 'method-map' || linkedMethodId ? 1 : 0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string | number;
    token: string;
    rateOwnerType?: 'trader' | 'merchant';
  } | null>(null);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(false);
  const [exchangeRatesError, setExchangeRatesError] = useState<string | null>(null);
  const [exchangeRateDialogOpen, setExchangeRateDialogOpen] = useState(false);
  const [selectedExchangeCurrencyId, setSelectedExchangeCurrencyId] = useState('');
  const [traderExchangeRates, setTraderExchangeRates] = useState<TraderExchangeRate[]>([]);
  const [traderExchangeRatesLoading, setTraderExchangeRatesLoading] = useState(false);
  const [traderExchangeRatesError, setTraderExchangeRatesError] = useState<string | null>(null);
  const [merchantExchangeRates, setMerchantExchangeRates] = useState<MerchantExchangeRate[]>([]);
  const [merchantExchangeRatesLoading, setMerchantExchangeRatesLoading] = useState(false);
  const [merchantExchangeRatesError, setMerchantExchangeRatesError] = useState<string | null>(null);
  const [traderExchangeRateDialogOpen, setTraderExchangeRateDialogOpen] = useState(false);
  const [selectedExchangeRateOwnerType, setSelectedExchangeRateOwnerType] = useState<'trader' | 'merchant'>('trader');
  const [selectedTraderExchangeCurrencyId, setSelectedTraderExchangeCurrencyId] = useState('');
  const [selectedTraderExchangeTraderId, setSelectedTraderExchangeTraderId] = useState('');
  const [selectedMerchantExchangeMerchantId, setSelectedMerchantExchangeMerchantId] = useState('');
  const [selectedTraderExchangeRate, setSelectedTraderExchangeRate] = useState<TraderExchangeRate | null>(null);
  const [selectedMerchantExchangeRate, setSelectedMerchantExchangeRate] = useState<MerchantExchangeRate | null>(null);
  const [traderExchangeRateValue, setTraderExchangeRateValue] = useState('');
  const [traders, setTraders] = useState<Trader[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [outSettingsDialogOpen, setOutSettingsDialogOpen] = useState(false);
  const [outSettingsDialogMode, setOutSettingsDialogMode] = useState<'create' | 'edit'>('create');
  const [outSettingsPaymentSystemId, setOutSettingsPaymentSystemId] = useState<string | null>(null);
  const [inSettingsDialogOpen, setInSettingsDialogOpen] = useState(false);
  const [inSettingsDialogMode, setInSettingsDialogMode] = useState<'create' | 'edit'>('create');
  const [inSettingsPaymentSystemId, setInSettingsPaymentSystemId] = useState<string | null>(null);
  const [paymentSystemDirectionFilter, setPaymentSystemDirectionFilter] = useState<'IN' | 'OUT'>('IN');
  const wallets = useFinance({ type: 'wallets' });
  const accounts = useFinance({ type: 'accounts' });
  const currencies = useFinance({ type: 'currencies' });
  const countries = useFinance({ type: 'countries' });
  const [countriesData, setCountriesData] = useState<any[]>([]);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isClientManager = currentUser?.role === RoleEnum.CLIENT_MANAGER && !currentUser?.is_superuser;
  const { showNotification } = useNotification();

  const canCreateWallet = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_WALLETS_CREATE),
    [currentUser],
  );
  const canUpdateWallet = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_WALLETS_UPDATE),
    [currentUser],
  );
  const canDeleteWallet = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_WALLETS_DELETE),
    [currentUser],
  );

  const canCreateAccount = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_ACCOUNTS_CREATE),
    [currentUser],
  );
  const canUpdateAccount = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_ACCOUNTS_UPDATE),
    [currentUser],
  );
  const canDeleteAccount = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_ACCOUNTS_DELETE),
    [currentUser],
  );

  const canCreateCurrency = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_CURRENCIES_CREATE),
    [currentUser],
  );
  const canUpdateCurrency = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_CURRENCIES_UPDATE),
    [currentUser],
  );
  const canDeleteCurrency = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_CURRENCIES_DELETE),
    [currentUser],
  );

  const canCreateCountry = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_COUNTRIES_CREATE),
    [currentUser],
  );
  const canUpdateCountry = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_COUNTRIES_UPDATE),
    [currentUser],
  );
  const canDeleteCountry = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_COUNTRIES_DELETE),
    [currentUser],
  );

  const canCreateExchangeRate = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_EXCHANGE_RATES_CREATE),
    [currentUser],
  );
  const canDeleteExchangeRate = useMemo(
    () => canPerformAction(currentUser, ActionKey.FINANCE_EXCHANGE_RATES_DELETE),
    [currentUser],
  );

  const fetchExchangeRates = async () => {
    try {
      setExchangeRatesLoading(true);
      const response = await financeService.getExchangeRates();
      setExchangeRates(response);
      setExchangeRatesError(null);
    } catch (err: any) {
      setExchangeRatesError(err?.message || 'Ошибка при загрузке курсов валют');
    } finally {
      setExchangeRatesLoading(false);
    }
  };

  const fetchTraderExchangeRates = async () => {
    try {
      setTraderExchangeRatesLoading(true);
      const response = await financeService.getTraderExchangeRates();
      setTraderExchangeRates(response);
      setTraderExchangeRatesError(null);
    } catch (err: any) {
      setTraderExchangeRatesError(err?.message || 'Ошибка при загрузке курсов ЛК трейдера');
    } finally {
      setTraderExchangeRatesLoading(false);
    }
  };

  const fetchMerchantExchangeRates = async () => {
    try {
      setMerchantExchangeRatesLoading(true);
      setMerchantExchangeRates(await financeService.getMerchantExchangeRates());
      setMerchantExchangeRatesError(null);
    } catch (err: any) {
      setMerchantExchangeRatesError(err?.message || 'Ошибка при загрузке курсов мерчантов');
    } finally {
      setMerchantExchangeRatesLoading(false);
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await dictionariesService.getCountries();
        setCountriesData(response);
      } catch (err) {
        console.error(err);
      }
    };
    const fetchTraders = async () => {
      try {
        const response = await tradersService.getTraders({ is_archive: false, limit: 1000 });
        setTraders(Array.isArray(response) ? response : response?.items || []);
      } catch (err) {
        console.error(err);
      }
    };
    const fetchMerchants = async () => {
      try {
        const response = await merchantsService.getMerchants({ limit: 1000 });
        setMerchants(Array.isArray(response) ? response : response?.items || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCountries();
    fetchTraders();
    fetchMerchants();
    fetchExchangeRates();
    fetchTraderExchangeRates();
    fetchMerchantExchangeRates();
  }, []);

  const filteredPaymentSystems = useMemo(() => {
    const list = wallets.data || [];
    if (paymentSystemDirectionFilter === 'OUT') {
      return list.filter((row: { supports_payout_out?: boolean }) => row.supports_payout_out === true);
    }
    return list.filter((row: { supports_payout_out?: boolean }) => row.supports_payout_out !== true);
  }, [wallets.data, paymentSystemDirectionFilter]);

  const availableExchangeCurrencies = useMemo(() => {
    const existingCurrencyIds = new Set(
      exchangeRates
        .map((rate: any) => rate.currency_id)
        .filter(Boolean),
    );

    const existingCurrencyCodes = new Set(
      exchangeRates
        .map((rate) => String(rate.currency || '').trim().toUpperCase())
        .filter(Boolean),
    );

    return (currencies.data || []).filter((currency: any) => {
      const hasRateById = existingCurrencyIds.has(currency.id);
      const hasRateByCode = existingCurrencyCodes.has(String(currency.iso_code || '').trim().toUpperCase());
      return !hasRateById && !hasRateByCode;
    });
  }, [exchangeRates, currencies.data]);

  const individualExchangeRates = useMemo<IndividualExchangeRateRow[]>(() => [
    ...traderExchangeRates.map((rate) => ({
      id: rate.id,
      owner_type: 'trader' as const,
      owner_id: rate.trader_id || null,
      owner_name: rate.trader?.name || 'Общий для трейдеров',
      currency_id: rate.currency_id,
      currency: rate.currency,
      rate: rate.rate,
      date_create: rate.date_create,
      date_update: rate.date_update,
      source: rate,
    })),
    ...merchantExchangeRates.map((rate) => ({
      id: rate.id,
      owner_type: 'merchant' as const,
      owner_id: rate.merchant_id,
      owner_name: rate.merchant?.name || rate.merchant_id,
      currency_id: rate.currency_id,
      currency: rate.currency,
      rate: rate.rate,
      date_create: rate.date_create,
      date_update: rate.date_update,
      source: rate,
    })),
  ], [merchantExchangeRates, traderExchangeRates]);

  const availableIndividualExchangeCurrencies = useMemo(() => {
    const selectedOwnerId = selectedExchangeRateOwnerType === 'trader'
      ? selectedTraderExchangeTraderId || null
      : selectedMerchantExchangeMerchantId;
    const existingCurrencyIds = new Set(
      individualExchangeRates
        .filter((rate) => (
          rate.owner_type === selectedExchangeRateOwnerType
          && rate.owner_id === selectedOwnerId
        ))
        .map((rate) => rate.currency_id)
        .filter(Boolean),
    );

    return (currencies.data || []).filter((currency: any) => !existingCurrencyIds.has(currency.id));
  }, [
    currencies.data,
    individualExchangeRates,
    selectedExchangeRateOwnerType,
    selectedMerchantExchangeMerchantId,
    selectedTraderExchangeTraderId,
  ]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const closeOutSettingsDialog = useCallback(() => {
    setOutSettingsDialogOpen(false);
    setOutSettingsPaymentSystemId(null);
    setOutSettingsDialogMode('create');
  }, []);

  const closeInSettingsDialog = useCallback(() => {
    setInSettingsDialogOpen(false);
    setInSettingsPaymentSystemId(null);
    setInSettingsDialogMode('create');
  }, []);

  const handleWalletDialogSubmit = async () => {
    const result = await wallets.handleSubmit();
    if (
      result &&
      typeof result === 'object' &&
      'createdPaymentSystem' in result &&
      result.createdPaymentSystem?.id
    ) {
      if (result.createdPaymentSystem.supports_payout_out === true) {
        setOutSettingsDialogMode('create');
        setOutSettingsPaymentSystemId(result.createdPaymentSystem.id);
        setOutSettingsDialogOpen(true);
      } else {
        setInSettingsDialogMode('create');
        setInSettingsPaymentSystemId(result.createdPaymentSystem.id);
        setInSettingsDialogOpen(true);
      }
    }
  };

  const handleEditOutPaymentSystemSettings = (item: { id: string; supports_payout_out?: boolean }) => {
    if (!canUpdateWallet) {
      showNotification('У вас нет прав для изменения настроек выплат OUT', 'error');
      return;
    }
    if (!item.id || item.supports_payout_out !== true) {
      return;
    }
    setOutSettingsDialogMode('edit');
    setOutSettingsPaymentSystemId(item.id);
    setOutSettingsDialogOpen(true);
  };

  const handleEditInPaymentSystemSettings = (item: { id: string; supports_payout_out?: boolean }) => {
    if (!canUpdateWallet) {
      showNotification('У вас нет прав для изменения настроек IN', 'error');
      return;
    }
    if (!item.id || item.supports_payout_out === true) {
      return;
    }
    setInSettingsDialogMode('edit');
    setInSettingsPaymentSystemId(item.id);
    setInSettingsDialogOpen(true);
  };

  const handleDeleteClick = (item: any) => {
    if (tabValue === 0 && !canDeleteWallet) {
      showNotification('У вас нет прав для удаления платёжных систем', 'error');
      return;
    }
    if (tabValue === 2 && !canDeleteAccount) {
      showNotification('У вас нет прав для удаления банков', 'error');
      return;
    }
    if (tabValue === 3 && !canDeleteCurrency) {
      showNotification('У вас нет прав для удаления валют', 'error');
      return;
    }
    if (tabValue === 4 && !canDeleteCountry) {
      showNotification('У вас нет прав для удаления стран', 'error');
      return;
    }
    if (tabValue === 5 && !canDeleteExchangeRate) {
      showNotification('У вас нет прав для удаления курсов валют', 'error');
      return;
    }
    if (tabValue === 6 && !canDeleteExchangeRate) {
      showNotification('У вас нет прав для удаления индивидуальных курсов', 'error');
      return;
    }
    setItemToDelete({
      id: item.id,
      token:
        tabValue === 0
          ? item.token
          : tabValue === 3
            ? item.iso_code
            : tabValue === 5
              ? `курс ${item.currency}`
              : tabValue === 6
                ? `индивидуальный курс ${item.currency?.iso_code || ''}`
                : item.name,
      rateOwnerType: tabValue === 6 ? item.owner_type : undefined,
    });
    setDeleteDialogOpen(true);
  };

  const handleCreateExchangeRate = async () => {
    if (!selectedExchangeCurrencyId) {
      showNotification('Выберите валюту', 'error');
      return;
    }

    try {
      await financeService.createExchangeRate({ currency_id: selectedExchangeCurrencyId });
      showNotification('Курс успешно создан', 'success');
      setExchangeRateDialogOpen(false);
      setSelectedExchangeCurrencyId('');
      await fetchExchangeRates();
    } catch (err: any) {
      showNotification(err?.message || 'Ошибка при создании курса валюты', 'error');
    }
  };

  const openCreateTraderExchangeRateDialog = () => {
    setSelectedTraderExchangeRate(null);
    setSelectedMerchantExchangeRate(null);
    setSelectedExchangeRateOwnerType('trader');
    setSelectedTraderExchangeCurrencyId('');
    setSelectedTraderExchangeTraderId('');
    setSelectedMerchantExchangeMerchantId('');
    setTraderExchangeRateValue('');
    setTraderExchangeRateDialogOpen(true);
  };

  const openEditTraderExchangeRateDialog = (rate: IndividualExchangeRateRow) => {
    setSelectedExchangeRateOwnerType(rate.owner_type);
    setSelectedTraderExchangeRate(
      rate.owner_type === 'trader' ? rate.source as TraderExchangeRate : null,
    );
    setSelectedMerchantExchangeRate(
      rate.owner_type === 'merchant' ? rate.source as MerchantExchangeRate : null,
    );
    setSelectedTraderExchangeCurrencyId(rate.currency_id);
    setSelectedTraderExchangeTraderId(rate.owner_type === 'trader' ? rate.owner_id || '' : '');
    setSelectedMerchantExchangeMerchantId(rate.owner_type === 'merchant' ? rate.owner_id || '' : '');
    setTraderExchangeRateValue(String(rate.rate ?? ''));
    setTraderExchangeRateDialogOpen(true);
  };

  const handleSaveTraderExchangeRate = async () => {
    const selectedRate = selectedTraderExchangeRate || selectedMerchantExchangeRate;
    const ownerId = selectedExchangeRateOwnerType === 'trader'
      ? selectedTraderExchangeTraderId
      : selectedMerchantExchangeMerchantId;
    if (!selectedTraderExchangeCurrencyId && !selectedRate) {
      showNotification('Выберите валюту', 'error');
      return;
    }
    if (selectedExchangeRateOwnerType === 'merchant' && !ownerId && !selectedRate) {
      showNotification('Выберите мерчанта', 'error');
      return;
    }
    const numericRate = Number(traderExchangeRateValue);
    if (!Number.isFinite(numericRate) || numericRate <= 0) {
      showNotification('Укажите положительный курс', 'error');
      return;
    }

    try {
      if (selectedTraderExchangeRate) {
        await financeService.updateTraderExchangeRate(selectedTraderExchangeRate.id, {
          rate: traderExchangeRateValue,
        });
      } else if (selectedMerchantExchangeRate) {
        await financeService.updateMerchantExchangeRate(selectedMerchantExchangeRate.id, {
          rate: traderExchangeRateValue,
        });
      } else if (selectedExchangeRateOwnerType === 'merchant') {
        await financeService.createMerchantExchangeRate({
          currency_id: selectedTraderExchangeCurrencyId,
          merchant_id: selectedMerchantExchangeMerchantId,
          rate: traderExchangeRateValue,
        });
      } else {
        await financeService.createTraderExchangeRate({
          currency_id: selectedTraderExchangeCurrencyId,
          trader_id: selectedTraderExchangeTraderId || null,
          rate: traderExchangeRateValue,
        });
      }
      showNotification('Индивидуальный курс сохранён', 'success');
      setTraderExchangeRateDialogOpen(false);
      await Promise.all([fetchTraderExchangeRates(), fetchMerchantExchangeRates()]);
    } catch (err: any) {
      showNotification(err?.message || 'Ошибка при сохранении индивидуального курса', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      if (tabValue === 0) {
        if (!canDeleteWallet) {
          showNotification('У вас нет прав для удаления платёжных систем', 'error');
          return;
        }
        await wallets.handleDelete(itemToDelete.id);
      } else if (tabValue === 2) {
        if (!canDeleteAccount) {
          showNotification('У вас нет прав для удаления банков', 'error');
          return;
        }
        await accounts.handleDelete(itemToDelete.id);
      } else if (tabValue === 3) {
        if (!canDeleteCurrency) {
          showNotification('У вас нет прав для удаления валют', 'error');
          return;
        }
        await currencies.handleDelete(itemToDelete.id);
      } else if (tabValue === 4) {
        if (!canDeleteCountry) {
          showNotification('У вас нет прав для удаления стран', 'error');
          return;
        }
        await countries.handleDelete(itemToDelete.id);
      } else if (tabValue === 5) {
        if (!canDeleteExchangeRate) {
          showNotification('У вас нет прав для удаления курсов валют', 'error');
          return;
        }
        await financeService.deleteExchangeRate(itemToDelete.id);
        await fetchExchangeRates();
      } else if (tabValue === 6) {
        if (!canDeleteExchangeRate) {
          showNotification('У вас нет прав для удаления индивидуальных курсов', 'error');
          return;
        }
        if (itemToDelete.rateOwnerType === 'merchant') {
          await financeService.deleteMerchantExchangeRate(String(itemToDelete.id));
        } else {
          await financeService.deleteTraderExchangeRate(String(itemToDelete.id));
        }
        await Promise.all([fetchTraderExchangeRates(), fetchMerchantExchangeRates()]);
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const walletColumns = [
    { field: 'token', label: 'Название' },
    {
      field: 'currency',
      label: 'Валюта',
      render: (value: any) => value?.iso_code || '-',
    },
    {
      field: 'supports_payout_out',
      label: 'Направление',
      render: (_value: unknown, row: { supports_payout_out?: boolean }) =>
        row.supports_payout_out ? 'OUT' : 'IN',
    },
  ];

  const accountColumns = [
    { field: 'name', label: 'Название' },
    { 
      field: 'country_name', 
      label: 'Страна',
      render: (value: any, row: any) => row.country?.name || '-'
    },
    { 
      field: 'country_code', 
      label: 'Код страны',
      render: (value: any, row: any) => row.country?.iso_code || '-'
    }
  ];

  const currencyColumns = [
    { field: 'iso_code', label: 'ISO код' }
  ];

  const countryColumns = [
    { field: 'name', label: 'Название' },
    { field: 'iso_code', label: 'ISO код' }
  ];

  if (isClientManager) {
    return (
      <AnimatedPage>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 4 }}>
            Финансы
          </Typography>
          <PaymentMethodMap initialSelectedMethodId={linkedMethodId} />
        </Box>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Финансы
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ display: tabValue === 1 ? 'none' : 'inline-flex' }}
            onClick={() => {
              switch (tabValue) {
                case 0:
                  if (!canCreateWallet) {
                    showNotification('У вас нет прав для создания платёжных систем', 'error');
                    return;
                  }
                  wallets.handleCreate();
                  break;
                case 2:
                  if (!canCreateAccount) {
                    showNotification('У вас нет прав для создания банков', 'error');
                    return;
                  }
                  accounts.handleCreate();
                  break;
                case 3:
                  if (!canCreateCurrency) {
                    showNotification('У вас нет прав для создания валют', 'error');
                    return;
                  }
                  currencies.handleCreate();
                  break;
                case 4:
                  if (!canCreateCountry) {
                    showNotification('У вас нет прав для создания стран', 'error');
                    return;
                  }
                  countries.handleCreate();
                  break;
                case 5:
                  if (!canCreateExchangeRate) {
                    showNotification('У вас нет прав для создания курсов валют', 'error');
                    return;
                  }
                  if (!availableExchangeCurrencies.length) {
                    showNotification('Для всех имеющихся валют курс уже создан', 'info');
                    return;
                  }
                  setSelectedExchangeCurrencyId('');
                  setExchangeRateDialogOpen(true);
                  break;
                case 6:
                  if (!canCreateExchangeRate) {
                    showNotification('У вас нет прав для создания индивидуальных курсов', 'error');
                    return;
                  }
                  openCreateTraderExchangeRateDialog();
                  break;
              }
            }}
            disabled={
              (tabValue === 0 && !canCreateWallet) ||
              (tabValue === 2 && !canCreateAccount) ||
              (tabValue === 3 && !canCreateCurrency) ||
              (tabValue === 4 && !canCreateCountry) ||
              (tabValue === 5 && !canCreateExchangeRate) ||
              (tabValue === 6 && !canCreateExchangeRate)
            }
          >
            {tabValue === 0 && 'Добавить платежную систему'}
            {tabValue === 2 && 'Добавить банк'}
            {tabValue === 3 && 'Добавить валюту'}
            {tabValue === 4 && 'Добавить страну'}
            {tabValue === 5 && 'Добавить курс'}
            {tabValue === 6 && 'Добавить индивидуальный курс'}
          </Button>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Платежные системы" />
            <Tab label="Карта методов" />
            <Tab label="Банки" />
            <Tab label="Валюты" />
            <Tab label="Страны" />
            <Tab label="Курсы валют" />
            <Tab label="Индивидуальные курсы" />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
            <Typography variant="h5">Платежные системы</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={paymentSystemDirectionFilter}
              onChange={(_e, value: 'IN' | 'OUT' | null) => {
                if (value !== null) {
                  setPaymentSystemDirectionFilter(value);
                }
              }}
              aria-label="Фильтр по направлению ПС"
            >
              <ToggleButton value="IN">IN</ToggleButton>
              <ToggleButton value="OUT">OUT</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <FinanceTable
            data={filteredPaymentSystems}
            columns={walletColumns}
            onEdit={(item) => {
              if (!canUpdateWallet) {
                showNotification('У вас нет прав для редактирования платёжных систем', 'error');
                return;
              }
              wallets.handleEdit(item);
            }}
            onEditOutSettings={handleEditOutPaymentSystemSettings}
            onEditInSettings={handleEditInPaymentSystemSettings}
            onDelete={handleDeleteClick}
          />
          <FinanceDialog
            open={wallets.dialogOpen}
            onClose={() => wallets.setDialogOpen(false)}
            onSubmit={handleWalletDialogSubmit}
            title={wallets.formData.id ? 'Редактировать платежную систему' : 'Создать платежную систему'}
            formData={wallets.formData}
            onFormChange={wallets.handleFormChange}
            currencies={wallets.currencies}
            countries={countriesData}
          />
          <PaymentSystemOutSettingsDialog
            open={outSettingsDialogOpen}
            paymentSystemId={outSettingsPaymentSystemId}
            mode={outSettingsDialogMode}
            canSave={outSettingsDialogMode === 'create' ? canCreateWallet : canUpdateWallet}
            onClose={closeOutSettingsDialog}
            onSuccess={() => {
              void wallets.refetch();
            }}
          />
          <PaymentSystemInSettingsDialog
            open={inSettingsDialogOpen}
            paymentSystemId={inSettingsPaymentSystemId}
            mode={inSettingsDialogMode}
            canSave={inSettingsDialogMode === 'create' ? canCreateWallet : canUpdateWallet}
            onClose={closeInSettingsDialog}
            onSuccess={() => {
              void wallets.refetch();
            }}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Банки</Typography>
          </Box>
          <FinanceTable
            data={accounts.data}
            columns={accountColumns}
            onEdit={(item) => {
              if (!canUpdateAccount) {
                showNotification('У вас нет прав для редактирования банков', 'error');
                return;
              }
              accounts.handleEdit(item);
            }}
            onDelete={handleDeleteClick}
          />
          <FinanceDialog
            open={accounts.dialogOpen}
            onClose={() => accounts.setDialogOpen(false)}
            onSubmit={accounts.handleSubmit}
            title={accounts.formData.id ? 'Редактировать банк' : 'Создать банк'}
            formData={accounts.formData}
            onFormChange={accounts.handleFormChange}
            currencies={accounts.currencies}
            countries={countriesData}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Валюты</Typography>
          </Box>
          <FinanceTable
            data={currencies.data}
            columns={currencyColumns}
            onEdit={(item) => {
              if (!canUpdateCurrency) {
                showNotification('У вас нет прав для редактирования валют', 'error');
                return;
              }
              currencies.handleEdit(item);
            }}
            onDelete={handleDeleteClick}
          />
          <FinanceDialog
            open={currencies.dialogOpen}
            onClose={() => currencies.setDialogOpen(false)}
            onSubmit={currencies.handleSubmit}
            title={currencies.formData.id ? 'Редактировать валюту' : 'Создать валюту'}
            formData={currencies.formData}
            onFormChange={currencies.handleFormChange}
            currencies={currencies.currencies}
            countries={countriesData}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Страны</Typography>
          </Box>
          <FinanceTable
            data={countries.data}
            columns={countryColumns}
            onEdit={(item) => {
              if (!canUpdateCountry) {
                showNotification('У вас нет прав для редактирования стран', 'error');
                return;
              }
              countries.handleEdit(item);
            }}
            onDelete={handleDeleteClick}
          />
          <FinanceDialog
            open={countries.dialogOpen}
            onClose={() => countries.setDialogOpen(false)}
            onSubmit={countries.handleSubmit}
            title={countries.formData.id ? 'Редактировать страну' : 'Создать страну'}
            formData={countries.formData}
            onFormChange={countries.handleFormChange}
            currencies={countries.currencies}
            countries={countriesData}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <ExchangeRates
            rates={exchangeRates}
            loading={exchangeRatesLoading}
            error={exchangeRatesError}
            onDelete={handleDeleteClick}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <IndividualExchangeRates
            rates={individualExchangeRates}
            loading={traderExchangeRatesLoading || merchantExchangeRatesLoading}
            error={traderExchangeRatesError || merchantExchangeRatesError}
            onEdit={openEditTraderExchangeRateDialog}
            onDelete={handleDeleteClick}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PaymentMethodMap />
        </TabPanel>
        <Dialog
          open={exchangeRateDialogOpen}
          onClose={() => setExchangeRateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Создать курс валюты</DialogTitle>
          <DialogContent>
            {!availableExchangeCurrencies.length ? (
              <Typography sx={{ mt: 1 }}>Для всех имеющихся валют курс уже создан</Typography>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel id="exchange-rate-currency-label">Валюта</InputLabel>
                <Select
                  labelId="exchange-rate-currency-label"
                  value={selectedExchangeCurrencyId}
                  label="Валюта"
                  onChange={(event) => setSelectedExchangeCurrencyId(event.target.value)}
                >
                  {availableExchangeCurrencies.map((currency: any) => (
                    <MenuItem key={currency.id} value={currency.id}>
                      {currency.iso_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExchangeRateDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleCreateExchangeRate}
              disabled={!selectedExchangeCurrencyId || !availableExchangeCurrencies.length}
            >
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={traderExchangeRateDialogOpen}
          onClose={() => setTraderExchangeRateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedTraderExchangeRate || selectedMerchantExchangeRate
              ? 'Редактировать индивидуальный курс'
              : 'Создать индивидуальный курс'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth disabled={Boolean(selectedTraderExchangeRate || selectedMerchantExchangeRate)}>
                <InputLabel id="individual-exchange-rate-owner-type-label">Тип получателя</InputLabel>
                <Select
                  labelId="individual-exchange-rate-owner-type-label"
                  value={selectedExchangeRateOwnerType}
                  label="Тип получателя"
                  onChange={(event) => {
                    setSelectedExchangeRateOwnerType(event.target.value as 'trader' | 'merchant');
                    setSelectedTraderExchangeTraderId('');
                    setSelectedMerchantExchangeMerchantId('');
                    setSelectedTraderExchangeCurrencyId('');
                  }}
                >
                  <MenuItem value="trader">Трейдер</MenuItem>
                  <MenuItem value="merchant">Мерчант</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={Boolean(selectedTraderExchangeRate || selectedMerchantExchangeRate)}>
                <InputLabel id="individual-exchange-rate-owner-label">
                  {selectedExchangeRateOwnerType === 'trader' ? 'Трейдер' : 'Мерчант'}
                </InputLabel>
                <Select
                  labelId="individual-exchange-rate-owner-label"
                  value={selectedExchangeRateOwnerType === 'trader'
                    ? selectedTraderExchangeTraderId
                    : selectedMerchantExchangeMerchantId}
                  label={selectedExchangeRateOwnerType === 'trader' ? 'Трейдер' : 'Мерчант'}
                  onChange={(event) => {
                    if (selectedExchangeRateOwnerType === 'trader') {
                      setSelectedTraderExchangeTraderId(event.target.value);
                    } else {
                      setSelectedMerchantExchangeMerchantId(event.target.value);
                    }
                    setSelectedTraderExchangeCurrencyId('');
                  }}
                >
                  {selectedExchangeRateOwnerType === 'trader' && (
                    <MenuItem value="">Общий курс для трейдеров</MenuItem>
                  )}
                  {(selectedExchangeRateOwnerType === 'trader' ? traders : merchants).map((owner) => (
                    <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={Boolean(selectedTraderExchangeRate || selectedMerchantExchangeRate)}>
                <InputLabel id="trader-exchange-rate-currency-label">Валюта</InputLabel>
                <Select
                  labelId="trader-exchange-rate-currency-label"
                  value={selectedTraderExchangeCurrencyId}
                  label="Валюта"
                  onChange={(event) => setSelectedTraderExchangeCurrencyId(event.target.value)}
                >
                  {(selectedTraderExchangeRate || selectedMerchantExchangeRate
                    ? [(selectedTraderExchangeRate || selectedMerchantExchangeRate)?.currency].filter(Boolean)
                    : availableIndividualExchangeCurrencies
                  ).map((currency: any) => (
                    <MenuItem key={currency.id} value={currency.id}>
                      {currency.iso_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Курс"
                type="number"
                value={traderExchangeRateValue}
                onChange={(event) => setTraderExchangeRateValue(event.target.value)}
                inputProps={{ step: '0.000001', min: '0.000001' }}
                fullWidth
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTraderExchangeRateDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleSaveTraderExchangeRate}
              disabled={
                !traderExchangeRateValue
                || (!(selectedTraderExchangeRate || selectedMerchantExchangeRate)
                  && (!selectedTraderExchangeCurrencyId
                    || (selectedExchangeRateOwnerType === 'trader'
                      ? false
                      : !selectedMerchantExchangeMerchantId)))
              }
            >
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemName={itemToDelete?.token || ''}
        />
      </Box>
    </AnimatedPage>
  );
};

export default Finance; 
